// Canvas fonts (visuals §5): cell names and mass are drawn in Ubuntu Regular, the face agar's
// look uses. The woff2 files ship in public/fonts (served from /fonts/) with their licence
// (public/fonts/UFL.txt, Ubuntu Font Licence 1.0). They are the Google Fonts subsets of the
// official Ubuntu release (fonts.gstatic.com, "Ubuntu" v21), split by unicode range so a page
// only downloads the scripts it draws: Regular in latin, latin-ext, cyrillic(-ext), greek(-ext),
// Bold in latin and latin-ext.
//
// The faces are registered from script with the FontFace API (`font-display: block`
// semantics: canvas text waits for the face instead of caching a fallback), so the renderer
// works whether or not index.html declares them. `loadCanvasFonts()` resolves once the Regular
// latin face is ready (or failed): the caller then clears its text bitmap cache.

export const CANVAS_FONT_FAMILY = 'Ubuntu';

const LATIN =
  'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, ' +
  'U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD';
const LATIN_EXT =
  'U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, ' +
  'U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C4, U+2113, ' +
  'U+2C60-2C7F, U+A720-A7FF';
const CYRILLIC = 'U+0301, U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116';
const CYRILLIC_EXT = 'U+0460-052F, U+1C80-1C8A, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F';
const GREEK = 'U+0370-0377, U+037A-037F, U+0384-038A, U+038C, U+038E-03A1, U+03A3-03FF';
const GREEK_EXT = 'U+1F00-1FFF';

export interface CanvasFontFace {
  readonly file: string;
  readonly weight: '400' | '700';
  readonly unicodeRange: string;
}

/** The bundled Ubuntu faces (public/fonts). */
export const UBUNTU_FACES: readonly CanvasFontFace[] = Object.freeze([
  { file: 'Ubuntu-Regular.woff2', weight: '400', unicodeRange: LATIN },
  { file: 'Ubuntu-Regular-ext.woff2', weight: '400', unicodeRange: LATIN_EXT },
  { file: 'Ubuntu-Regular-cyrillic.woff2', weight: '400', unicodeRange: CYRILLIC },
  { file: 'Ubuntu-Regular-cyrillic-ext.woff2', weight: '400', unicodeRange: CYRILLIC_EXT },
  { file: 'Ubuntu-Regular-greek.woff2', weight: '400', unicodeRange: GREEK },
  { file: 'Ubuntu-Regular-greek-ext.woff2', weight: '400', unicodeRange: GREEK_EXT },
  { file: 'Ubuntu-Bold.woff2', weight: '700', unicodeRange: LATIN },
  { file: 'Ubuntu-Bold-ext.woff2', weight: '700', unicodeRange: LATIN_EXT },
]);

interface FontEnv {
  readonly FontFace?: new (
    family: string,
    source: string,
    descriptors?: FontFaceDescriptors,
  ) => FontFace;
  readonly document?: { readonly fonts?: FontFaceSet };
}

let pending: Promise<boolean> | null = null;

/**
 * Registers the Ubuntu faces once per page and loads the Regular latin face. Resolves true when
 * it loaded, false when fonts are unavailable (Node, an old browser, a failed fetch); never
 * rejects. `baseUrl` is where public/fonts is served.
 */
export function loadCanvasFonts(baseUrl = '/fonts/', env: FontEnv = globalThis): Promise<boolean> {
  if (pending) return pending;
  const Face = env.FontFace;
  const fonts = env.document?.fonts;
  if (typeof Face !== 'function' || !fonts) {
    pending = Promise.resolve(false);
    return pending;
  }
  let first: FontFace | null = null;
  try {
    for (const f of UBUNTU_FACES) {
      const face = new Face(CANVAS_FONT_FAMILY, `url(${baseUrl}${f.file}) format('woff2')`, {
        weight: f.weight,
        style: 'normal',
        display: 'block',
        unicodeRange: f.unicodeRange,
      });
      fonts.add(face);
      first ??= face;
    }
  } catch {
    pending = Promise.resolve(false);
    return pending;
  }
  const face = first;
  pending = face
    ? face.load().then(
        () => true,
        () => false,
      )
    : Promise.resolve(false);
  return pending;
}

/** Forgets the registration (tests). */
export function resetCanvasFontsForTest(): void {
  pending = null;
}
